'use client';

import React from 'react';

import { datadogLogs } from '@datadog/browser-logs';

import { attemptChunkRecovery, isChunkLoadError } from '@/utils/chunkError';

type RootErrorBoundaryProps = {
  children: React.ReactNode;
};

type RootErrorBoundaryState = {
  hasError: boolean;
  isRecovering: boolean;
};

export class RootErrorBoundary extends React.Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = { hasError: false, isRecovering: false };

  static getDerivedStateFromError(): Partial<RootErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    try {
      const meta = {
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        componentStack: info?.componentStack,
        isChunkError: isChunkLoadError(error),
      };
      // Log to console for quick visibility
      // eslint-disable-next-line no-console
      console.error('[RootErrorBoundary] Uncaught error', error, meta);

      // Forward to Datadog if available
      try {
        datadogLogs.logger.error(
          '[RootErrorBoundary] Uncaught error',
          meta,
          error instanceof Error ? error : new Error(String(error)),
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_) {
        // ignore if logs not initialized yet
      }

      if (meta.isChunkError && attemptChunkRecovery('root-boundary')) {
        this.setState({ isRecovering: true });
      }
    } catch {
      // no-op
    }
  }

  render() {
    if (this.state.hasError && this.state.isRecovering) {
      return null;
    }

    // Do not render a fallback UI to avoid masking production issues.
    // Just keep rendering children; if the tree is broken, Next.js route-level boundaries may show their own UI.
    return this.props.children as React.ReactElement;
  }
}
